'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  DocumentTextIcon, 
  ChartBarIcon, 
  MagnifyingGlassIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import StockNotesModal from './StockNotesModal';
import { watchlistApi } from '@/lib/api/watchlistApi';

interface StockActionIconsProps {
  isin: string;
  stockName: string;
  tradingViewLink?: string;
  screenerLink?: string;
  watchlistId?: string;
  onStockDetailClick?: (isin: string) => void;
  currentPrice?: number;
  showNotesCount?: boolean;
}

// Global queue management to prevent server overload
class NotesAPIQueue {
  private static instance: NotesAPIQueue;
  private queue: Array<{ isin: string; resolve: (count: number) => void; reject: (error: Error | unknown) => void }> = [];
  private processing = false;
  private readonly DELAY_BETWEEN_CALLS = 100; // 100ms delay between API calls

  static getInstance(): NotesAPIQueue {
    if (!NotesAPIQueue.instance) {
      NotesAPIQueue.instance = new NotesAPIQueue();
    }
    return NotesAPIQueue.instance;
  }

  async addToQueue(isin: string): Promise<number> {
    return new Promise((resolve, reject) => {
      this.queue.push({ isin, resolve, reject });
      this.processQueue();
    });
  }

  private async processQueue() {
    if (this.processing || this.queue.length === 0) return;
    
    this.processing = true;
    
    while (this.queue.length > 0) {
      const item = this.queue.shift();
      if (!item) continue;

      try {
        const response = await watchlistApi.getNotes(item.isin);
        item.resolve(response.notes.length);
        
        // Add delay to prevent overwhelming the server
        if (this.queue.length > 0) {
          await new Promise(resolve => setTimeout(resolve, this.DELAY_BETWEEN_CALLS));
        }
      } catch (error) {
        console.error(`Error loading notes count for ${item.isin}:`, error);
        item.reject(error);
      }
    }
    
    this.processing = false;
  }
}

export default function StockActionIcons({
  isin,
  stockName,
  tradingViewLink,
  screenerLink,
  watchlistId,
  onStockDetailClick,
  currentPrice,
  showNotesCount = true
}: StockActionIconsProps) {
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [notesCount, setNotesCount] = useState<number>(0);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  const elementRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Intersection Observer to detect when the component is visible
  useEffect(() => {
    if (!showNotesCount || !elementRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !notesLoaded) {
            setIsVisible(true);
          }
        });
      },
      {
        root: null,
        rootMargin: '50px', // Start loading when element is 50px away from viewport
        threshold: 0.1, // Trigger when 10% of element is visible
      }
    );

    observerRef.current.observe(elementRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [showNotesCount, notesLoaded]);

  // Load notes count when component becomes visible
  useEffect(() => {
    if (!isVisible || notesLoaded || !showNotesCount || !isin) return;

    const loadNotesCount = async () => {
      setNotesLoading(true);
      setNotesLoaded(true); // Mark as loaded to prevent duplicate calls
      
      try {
        const queue = NotesAPIQueue.getInstance();
        const count = await queue.addToQueue(isin);
        setNotesCount(count);
      } catch (err) {
        console.error('Error loading notes count:', err);
        setNotesCount(0);
      } finally {
        setNotesLoading(false);
      }
    };

    loadNotesCount();
  }, [isVisible, notesLoaded, showNotesCount, isin]);

  const handleNotesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsNotesModalOpen(true);
  };

  const handleTradingViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (tradingViewLink) {
      window.open(tradingViewLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleScreenerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (screenerLink) {
      window.open(screenerLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleStockDetailClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onStockDetailClick) {
      onStockDetailClick(isin);
    } else {
      window.open(`/stocks/detail/${isin}`, '_blank', 'noopener,noreferrer');
    }
  };

  const refreshNotesCount = useCallback(async () => {
    if (!showNotesCount || !isin) return;
    
    try {
      const response = await watchlistApi.getNotes(isin);
      setNotesCount(response.notes.length);
    } catch (err) {
      console.error('Error refreshing notes count:', err);
    }
  }, [showNotesCount, isin]);

  return (
    <div ref={elementRef} className="flex items-center justify-start gap-0.5 mb-1 -ml-1.5 relative z-[20]">
      {/* Notes Icon with Count */}
      <div className="relative">
        <button
          onClick={handleNotesClick}
          className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-500 dark:hover:text-white dark:hover:bg-zinc-800 rounded-md transition-colors"
          title={`Notes${notesCount > 0 ? ` (${notesCount})` : ''}`}
        >
          <DocumentTextIcon className="h-4 w-4" />
        </button>
        {showNotesCount && notesLoading && (
          <span className="absolute -top-1 -right-1 bg-zinc-400 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          </span>
        )}
        {showNotesCount && !notesLoading && notesCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-emerald-700 dark:bg-emerald-400 text-white dark:text-zinc-950 text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center font-bold">
            {notesCount > 9 ? '9+' : notesCount}
          </span>
        )}
      </div>

      {/* Stock Details Icon */}
      <button
        onClick={handleStockDetailClick}
        className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-500 dark:hover:text-white dark:hover:bg-zinc-800 rounded-md transition-colors"
        title="Stock Details"
      >
        <InformationCircleIcon className="h-4 w-4" />
      </button>

      {/* TradingView Icon */}
      {tradingViewLink && (
        <button
          onClick={handleTradingViewClick}
          className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-500 dark:hover:text-white dark:hover:bg-zinc-800 rounded-md transition-colors relative z-[30]"
          title="TradingView"
        >
          <ChartBarIcon className="h-4 w-4" />
        </button>
      )}

      {/* Screener Icon */}
      {screenerLink && (
        <button
          onClick={handleScreenerClick}
          className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-500 dark:hover:text-white dark:hover:bg-zinc-800 rounded-md transition-colors relative z-[30]"
          title="Screener"
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
        </button>
      )}


      <StockNotesModal
        isOpen={isNotesModalOpen}
        onClose={() => {
          setIsNotesModalOpen(false);
          // Refresh notes count after modal closes
          setTimeout(refreshNotesCount, 500);
        }}
        isin={isin}
        stockName={stockName}
        watchlistId={watchlistId}
        currentPrice={currentPrice}
      />
    </div>
  );
} 